import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Users, UserPlus, Shield, Wrench, Mail, Trash2, Crown, SlidersHorizontal, ChevronDown, ChevronUp } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_INFO = {
  owner: { label: 'Owner', color: 'bg-[#4B2D6E] text-white', icon: Crown },
  full_admin: { label: 'Full Admin', color: 'bg-emerald-100 text-emerald-800', icon: Shield },
  limited_access: { label: 'Limited Access', color: 'bg-blue-100 text-blue-800', icon: SlidersHorizontal },
  va: { label: 'Team Member', color: 'bg-blue-100 text-blue-800', icon: Users },
  support: { label: 'Support/Builder', color: 'bg-amber-100 text-amber-800', icon: Wrench },
};

const PERMISSION_LABELS = {
  advance_candidates: { label: 'Advance/Decline Candidates', desc: 'Approve, decline, or hire candidates in the pipeline' },
  manage_interviews: { label: 'Manage Interview Schedules', desc: 'Create, edit, and delete interview time slots' },
  edit_candidates: { label: 'Edit Candidate Records', desc: 'Update candidate info, add notes, change ratings' },
  send_offers: { label: 'Send Offer Letters', desc: 'Create and send offer letters to candidates' },
  view_analytics: { label: 'View Analytics', desc: 'Access the Analytics dashboard and reports' },
  manage_jobs: { label: 'Manage Job Listings', desc: 'Create, edit, and delete job postings' },
  export_data: { label: 'Export Data', desc: 'Download and export candidate data as CSV' },
};

const TeamAccessTab = ({
  team, inviteDialogOpen, setInviteDialogOpen,
  inviteData, setInviteData, inviteUser,
  resendInvite, resetTeamPassword, removeTeamMember,
  fetchData
}) => {
  const [expandedUser, setExpandedUser] = useState(null);
  const [savingPerms, setSavingPerms] = useState(null);

  const toggleExpand = (userId) => {
    setExpandedUser(expandedUser === userId ? null : userId);
  };

  const updateRole = async (userId, newRole) => {
    setSavingPerms(userId);
    try {
      await axios.put(`${API}/auth/team/${userId}/permissions`, { role: newRole });
      toast.success(`Role updated to ${ROLE_INFO[newRole]?.label}`);
      if (fetchData) fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    } finally {
      setSavingPerms(null);
    }
  };

  const togglePermission = async (userId, permKey, currentPerms) => {
    setSavingPerms(userId);
    const updated = { ...currentPerms, [permKey]: !currentPerms[permKey] };
    try {
      await axios.put(`${API}/auth/team/${userId}/permissions`, {
        role: 'limited_access',
        permissions: updated
      });
      toast.success('Permission updated');
      if (fetchData) fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setSavingPerms(null);
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div><CardTitle>Team Access & Permissions</CardTitle><CardDescription>Manage who can access the dashboard and what they can do</CardDescription></div>
        <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#E8722A] hover:bg-[#D6621F]" data-testid="invite-user-btn"><UserPlus className="mr-2 h-4 w-4" />Invite Team Member</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Invite Team Member</DialogTitle><DialogDescription>Add a new team member and set their access level</DialogDescription></DialogHeader>
            <form onSubmit={inviteUser} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="invite_role">Access Level</Label>
                <Select value={inviteData.role} onValueChange={(value) => setInviteData({ ...inviteData, role: value })}>
                  <SelectTrigger data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="full_admin"><div className="flex items-center gap-2"><Shield className="h-4 w-4 text-emerald-600" />Full Admin</div></SelectItem>
                    <SelectItem value="limited_access"><div className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4 text-blue-600" />Limited Access</div></SelectItem>
                    <SelectItem value="support"><div className="flex items-center gap-2"><Wrench className="h-4 w-4 text-amber-600" />Support / Builder</div></SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label htmlFor="invite_name">Name</Label><Input id="invite_name" value={inviteData.name} onChange={(e) => setInviteData({ ...inviteData, name: e.target.value })} placeholder="Full name" required data-testid="invite-name-input" /></div>
              <div className="space-y-2"><Label htmlFor="invite_email">Email</Label><Input id="invite_email" type="email" value={inviteData.email} onChange={(e) => setInviteData({ ...inviteData, email: e.target.value })} placeholder="email@example.com" required data-testid="invite-email-input" /></div>

              <div className="bg-[#F5F5F5] p-4 rounded-lg text-sm">
                {inviteData.role === 'full_admin' && (
                  <>
                    <p className="font-semibold text-emerald-700 mb-2 flex items-center gap-2"><Shield className="h-4 w-4" /> Full Admin Permissions:</p>
                    <ul className="list-disc list-inside space-y-1 text-[#666]">
                      <li>Full access to all candidate operations</li>
                      <li>Manage interview schedules and job listings</li>
                      <li>Send offer letters and export data</li>
                      <li>View analytics and reports</li>
                      <li className="text-red-600">Cannot manage other team members' permissions</li>
                    </ul>
                  </>
                )}
                {inviteData.role === 'limited_access' && (
                  <>
                    <p className="font-semibold text-blue-700 mb-2 flex items-center gap-2"><SlidersHorizontal className="h-4 w-4" /> Limited Access (Customizable):</p>
                    <ul className="list-disc list-inside space-y-1 text-[#666]">
                      <li>Base: View candidate profiles and send messages</li>
                      <li>You can toggle specific permissions on/off after inviting</li>
                      <li className="text-[#4B2D6E] font-medium">Customize exactly what this person can do</li>
                    </ul>
                  </>
                )}
                {inviteData.role === 'support' && (
                  <>
                    <p className="font-semibold text-amber-700 mb-2 flex items-center gap-2"><Wrench className="h-4 w-4" /> Support/Builder:</p>
                    <ul className="list-disc list-inside space-y-1 text-[#666]">
                      <li>Technical platform access for troubleshooting</li>
                      <li>Manage settings, view candidates for diagnostics</li>
                      <li className="text-red-600">Cannot advance, decline, or hire candidates</li>
                    </ul>
                    <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded">
                      <p className="text-amber-700 text-xs flex items-center gap-2"><Shield className="h-3.5 w-3.5" /> Login alerts sent to you. All actions logged.</p>
                    </div>
                  </>
                )}
              </div>
              <Button type="submit" className="w-full bg-[#E8722A] hover:bg-[#D6621F]" data-testid="send-invite-btn">Send Invitation</Button>
            </form>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {/* Role Legend */}
        <div className="mb-6 p-4 bg-[#F5F5F5] rounded-lg">
          <h4 className="font-semibold text-[#4B2D6E] mb-3">Access Levels</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
            <div className="p-3 bg-white rounded border-l-4 border-[#4B2D6E]">
              <div className="flex items-center gap-2 font-medium text-[#4B2D6E]"><Crown className="h-4 w-4" /> Owner</div>
              <p className="text-[#666] mt-1">Full access + manage permissions</p>
            </div>
            <div className="p-3 bg-white rounded border-l-4 border-emerald-500">
              <div className="flex items-center gap-2 font-medium text-emerald-700"><Shield className="h-4 w-4" /> Full Admin</div>
              <p className="text-[#666] mt-1">Everything except team management</p>
            </div>
            <div className="p-3 bg-white rounded border-l-4 border-blue-500">
              <div className="flex items-center gap-2 font-medium text-blue-700"><SlidersHorizontal className="h-4 w-4" /> Limited Access</div>
              <p className="text-[#666] mt-1">Custom permissions per user</p>
            </div>
            <div className="p-3 bg-white rounded border-l-4 border-amber-500">
              <div className="flex items-center gap-2 font-medium text-amber-700"><Wrench className="h-4 w-4" /> Support</div>
              <p className="text-[#666] mt-1">Technical access only</p>
            </div>
          </div>
        </div>

        {/* Team Members List */}
        <div className="space-y-3">
          {team.map((member) => {
            const info = ROLE_INFO[member.role] || ROLE_INFO.va;
            const isExpanded = expandedUser === member.id;
            const isLimited = member.role === 'limited_access';
            const perms = member.permissions || {};

            return (
              <div key={member.id} className="border rounded-lg overflow-hidden" data-testid={`team-member-${member.id}`}>
                <div className="flex items-center justify-between p-4 bg-white">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold ${
                      member.role === 'owner' ? 'bg-[#4B2D6E]' :
                      member.role === 'full_admin' ? 'bg-emerald-500' :
                      member.role === 'limited_access' ? 'bg-blue-500' :
                      member.role === 'support' ? 'bg-amber-500' : 'bg-blue-500'
                    }`}>{member.name?.charAt(0).toUpperCase()}</div>
                    <div>
                      <p className="font-medium">{member.name}</p>
                      <p className="text-sm text-[#666]">{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {member.role !== 'owner' ? (
                      <Select
                        value={member.role}
                        onValueChange={(val) => updateRole(member.id, val)}
                        disabled={savingPerms === member.id}
                      >
                        <SelectTrigger className="w-40" data-testid={`role-select-${member.id}`}>
                          <Badge className={info.color}>{info.label}</Badge>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="full_admin"><div className="flex items-center gap-2"><Shield className="h-4 w-4 text-emerald-600" />Full Admin</div></SelectItem>
                          <SelectItem value="limited_access"><div className="flex items-center gap-2"><SlidersHorizontal className="h-4 w-4 text-blue-600" />Limited Access</div></SelectItem>
                          <SelectItem value="support"><div className="flex items-center gap-2"><Wrench className="h-4 w-4 text-amber-600" />Support</div></SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge className={info.color}>{info.label}</Badge>
                    )}

                    {isLimited && (
                      <Button variant="ghost" size="sm" onClick={() => toggleExpand(member.id)} className="text-[#4B2D6E]" data-testid={`toggle-perms-${member.id}`}>
                        {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </Button>
                    )}

                    {member.role !== 'owner' && (
                      <>
                        <Button variant="outline" size="sm" onClick={() => resendInvite(member.id)} className="text-[#4B2D6E] border-[#4B2D6E]/30 hover:bg-[#4B2D6E]/5" data-testid={`resend-invite-${member.id}`}><Mail className="h-3.5 w-3.5 mr-1" /> Resend</Button>
                        <Button variant="outline" size="sm" onClick={() => resetTeamPassword(member.id)} className="text-[#E8722A] border-[#E8722A]/30 hover:bg-[#E8722A]/5" data-testid={`reset-password-${member.id}`}><Shield className="h-3.5 w-3.5 mr-1" /> Reset</Button>
                        <Button variant="ghost" size="icon" onClick={() => removeTeamMember(member.id)} className="text-red-600 hover:text-red-700" data-testid={`remove-member-${member.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </>
                    )}
                  </div>
                </div>

                {/* Expandable Permission Panel for Limited Access */}
                {isLimited && isExpanded && (
                  <div className="px-4 pb-4 bg-blue-50/50 border-t" data-testid={`perms-panel-${member.id}`}>
                    <p className="text-sm font-medium text-blue-800 py-3">Permissions for {member.name}</p>
                    <div className="grid grid-cols-1 gap-2">
                      {Object.entries(PERMISSION_LABELS).map(([key, { label, desc }]) => (
                        <div key={key} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                          <div>
                            <p className="font-medium text-sm text-[#333]">{label}</p>
                            <p className="text-xs text-[#666]">{desc}</p>
                          </div>
                          <Switch
                            checked={perms[key] || false}
                            onCheckedChange={() => togglePermission(member.id, key, perms)}
                            disabled={savingPerms === member.id}
                            data-testid={`perm-${member.id}-${key}`}
                          />
                        </div>
                      ))}
                    </div>
                    <p className="text-xs text-blue-600 mt-3">Base permissions (always on): View candidate profiles, Send messages</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export { TeamAccessTab };
